import { createContext, useContext, ReactNode, useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './AuthContext';
import { getBrowserTimezone } from '@/utils/formatShopTime';
import { isValidIanaTimezone } from '@/utils/shopTime';

interface TimezoneContextType {
  shopTimezone: string;
  isLoading: boolean;
  // L-4: Exposed so consumers (Dashboard) can show a warning banner when TZ is misconfigured
  isTimezoneValid: boolean;
}

const TimezoneContext = createContext<TimezoneContextType | undefined>(undefined);

export const TimezoneProvider = ({ children }: { children: ReactNode }) => {
  const { activeShop } = useAuth();
  // Use browser timezone as fallback instead of hardcoded Pacific
  const [shopTimezone, setShopTimezone] = useState(() => getBrowserTimezone());
  const [isLoading, setIsLoading] = useState(true);
  // L-4: Track whether the DB timezone string is a valid IANA identifier
  const [isTimezoneValid, setIsTimezoneValid] = useState(true);

  useEffect(() => {
    const loadShopTimezone = async () => {
      if (!activeShop?.id) {
        setIsLoading(false);
        return;
      }

      try {
        const { data, error } = await supabase
          .from('shop_profile')
          .select('time_zone')
          .eq('shop_id', activeShop.id)
          .maybeSingle();

        if (error) throw error;
        
        if (data?.time_zone) {
          // L-4: Validate before applying — an invalid IANA TZ string (e.g., typo "America/Los-Angeles")
          // would cause shopLocalToUtc to throw and silently write "now" to the DB for every ticket.
          if (isValidIanaTimezone(data.time_zone)) {
            setShopTimezone(data.time_zone);
            setIsTimezoneValid(true);
          } else {
            console.error('[TimezoneContext] Invalid IANA timezone from shop_profile:', data.time_zone,
              '— keeping browser fallback to prevent scheduling corruption.');
            setIsTimezoneValid(false);
            // Keep browser fallback (already initialized) — it's at least a valid IANA string
          }
        } else {
          // Null/missing TZ → browser fallback is acceptable, not an error
          setIsTimezoneValid(true);
        }
      } catch (error) {
        console.error('Error loading shop timezone:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadShopTimezone();
  }, [activeShop?.id]);

  return (
    <TimezoneContext.Provider value={{ shopTimezone, isLoading, isTimezoneValid }}>
      {children}
    </TimezoneContext.Provider>
  );
};

export const useShopTimezone = () => {
  const context = useContext(TimezoneContext);
  if (!context) {
    throw new Error('useShopTimezone must be used within a TimezoneProvider');
  }
  return context;
};
